import { useEffect, useRef, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { InstantMode } from "@/components/InstantMode";
import { SituationEngine } from "@/components/SituationEngine";
import { OneMinute } from "@/components/OneMinute";
import { AIInterpreter } from "@/components/AIInterpreter";
import { AccessibilityMode } from "@/components/AccessibilityMode";
import { LifeCard } from "@/components/LifeCard";
import { TrustCircle } from "@/components/TrustCircle";
import { SafetyMap } from "@/components/SafetyMap";
import { Vision } from "@/components/Vision";
import { DemoMode } from "@/components/DemoMode";
import { JudgeMode } from "@/components/JudgeMode";
import { Footer } from "@/components/Footer";
import { ArrowRight, Sparkles } from "lucide-react";

export default function Home() {
  const [instantOpen, setInstantOpen] = useState(false);
  const [instantId, setInstantId] = useState(null);
  const [seedText, setSeedText] = useState("");
  const interpreterRef = useRef(null);

  const openInstant = (id) => {
    setInstantId(id);
    setInstantOpen(true);
  };

  const runPreset = (preset) => {
    setSeedText(preset.text);
    document.getElementById("interpreter")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-[color:var(--lg-void)] text-[color:var(--lg-text)]">
      <Navbar />

      <main id="instant">
        <Hero onPick={openInstant} onExplore={() => scrollTo("engine")} />

        <DemoMode onPreset={runPreset} />

        <SituationEngine onOpenInstant={openInstant} />

        <OneMinute />

        <AIInterpreter initialText={seedText} key={seedText} />

        <AccessibilityMode />

        <LifeCard />

        <TrustCircle />

        <SafetyMap />

        <Vision />

        <FinalCTA onOpenInstant={() => openInstant("emergency")} onExplore={() => scrollTo("engine")} />
      </main>

      <Footer />
      <JudgeMode />

      <InstantMode open={instantOpen} situationId={instantId} onClose={() => setInstantOpen(false)} />
    </div>
  );
}

function FinalCTA({ onOpenInstant, onExplore }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/5">
      <div className="absolute inset-0 tac-grid pointer-events-none opacity-40" />
      <div
        className="absolute inset-x-0 bottom-0 h-[380px] pointer-events-none opacity-[0.35]"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 100%, rgba(0,229,255,0.18) 0%, rgba(204,255,0,0.05) 40%, transparent 70%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)] flex items-center justify-center gap-2">
          <Sparkles size={14} /> STEP 12 // FINAL
        </div>
        <h2 className="mt-4 font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tight">
          Ready for the <span className="text-lime-accent">unexpected?</span>
        </h2>
        <p className="mt-4 max-w-2xl mx-auto text-lg text-[color:var(--lg-text-2)]">
          You don&apos;t need to know everything. You need to know what to do next.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <button className="btn-tactile primary" onClick={onOpenInstant} data-testid="final-enter">
            Enter LIFEGRID <ArrowRight size={14} />
          </button>
          <button className="btn-tactile" onClick={onExplore} data-testid="final-try">
            Try a scenario
          </button>
        </div>
      </div>
    </section>
  );
}
